import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import RPZenDude from './../../assets/images/RP_zen_dude.svg'
import RPLogoBlack from './../../assets/images/RPLogoB.svg'
import Typography from '@mui/material/Typography'
import { useWindowSizeUp } from '../../lib/utils'
import { primaryIconSize, theme } from '../../lib/styles/universal'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { IoAlertCircle, IoCheckmarkCircle } from 'react-icons/io5'
import { InterestForm } from '../organisms'
import { InterestStripeSetup } from '../organisms/InterestStripeSetup'
import { StripeContainer } from '../organisms/StripeContainer'
import { InterestPricingQuote } from '../molecules'
import {
    buildInterestSubmissionPricingQuote,
    computeInterestDemoPricingSummary,
    computePricingQuote,
    createInterestRequest,
    createInterestSetupIntent,
    formatInterestPricingAmount,
    getInterestCommunitySizeLabel,
    toast,
} from '../../lib/utils'
import {
    InterestedOrganizationDTO,
    isDemoInterestRequest,
} from '@hazadapt-git/public-core-base'
import { useSearchParams } from 'react-router-dom'
import { StripeElementsOptionsClientSecret } from '@stripe/stripe-js'

type InterestStep = 'form' | 'payment' | 'success'

interface InterestPageTemplateProps {}

export const InterestPageTemplate: FC<InterestPageTemplateProps> = () => {
    const { classes: localClasses } = useLocalStyles()
    const mdScreens = useWindowSizeUp('md')
    const [searchParams] = useSearchParams()
    const contentsScrollRef = useRef<HTMLDivElement>(null)

    const handleHeroWheel = useCallback((event: React.WheelEvent) => {
        const scrollEl = contentsScrollRef.current
        if (!scrollEl) return

        event.preventDefault()
        scrollEl.scrollBy({ top: event.deltaY })
    }, [])

    const [step, setStep] = useState<InterestStep>('form')
    const [stripeOptions, setStripeOptions] =
        useState<StripeElementsOptionsClientSecret | null>(null)
    const [submittedValues, setSubmittedValues] =
        useState<InterestedOrganizationDTO | null>(null)

    const pricingQuote = useMemo(() => {
        if (!submittedValues) return null
        if (isDemoInterestRequest(submittedValues.subscription_type)) {
            return null
        }
        return computePricingQuote(submittedValues.community_size)
    }, [submittedValues])

    const demoPricingSummary = useMemo(() => {
        if (
            !submittedValues ||
            !isDemoInterestRequest(submittedValues.subscription_type)
        ) {
            return null
        }
        return computeInterestDemoPricingSummary(
            submittedValues.community_size
        )
    }, [submittedValues])

    // Handle redirect return from Stripe (wallet / bank account flows)
    useEffect(() => {
        if (searchParams.get('setup_complete') === 'true') {
            setStep('success')
        }
    }, [searchParams])

    const handleInterestSubmit = useCallback(
        async (values: InterestedOrganizationDTO) => {
            try {
                const isDemoRequest = isDemoInterestRequest(
                    values.subscription_type
                )
                let stripeCustomerId: string | null = null
                let clientSecret: string | null = null

                if (!isDemoRequest) {
                    try {
                        const setupResult = await createInterestSetupIntent(
                            values.official_email,
                            values.organization_name
                        )
                        stripeCustomerId = setupResult.customer.id
                        clientSecret =
                            setupResult.setupIntent.client_secret ?? null
                    } catch (stripeErr) {
                        console.warn(
                            'Unable to create Stripe setup intent — proceeding without payment step',
                            stripeErr
                        )
                    }
                }

                await createInterestRequest({
                    ...values,
                    stripe_customer_id: stripeCustomerId,
                    pricing_quote: buildInterestSubmissionPricingQuote(values),
                })

                setSubmittedValues(values)

                if (!isDemoRequest && clientSecret) {
                    setStripeOptions({ clientSecret })
                    setStep('payment')
                } else {
                    setStep('success')
                }
            } catch (err) {
                console.error(err)
                toast(
                    'Unable to submit your interest form right now. Please retry.',
                    <IoAlertCircle
                        color={theme.palette.error.main}
                        size={primaryIconSize}
                    />,
                    'error'
                )
            }
        },
        []
    )

    const handleStartNew = useCallback(() => {
        setStep('form')
        setStripeOptions(null)
        setSubmittedValues(null)
    }, [])

    return (
        <div className={localClasses.container}>
            <div className={localClasses.mainContent}>
                {mdScreens ? (
                    <div
                        className={localClasses.heroColumn}
                        onWheel={handleHeroWheel}
                    >
                        <img
                            src={RPZenDude}
                            alt="Emergency manager meditating above his desk"
                            className={localClasses.heroImage}
                        />
                    </div>
                ) : null}
                <div className={localClasses.contents}>
                    <img
                        src={RPLogoBlack}
                        className={localClasses.rpLogo}
                        alt="ResiliencePoint logo"
                    />
                    <div className={localClasses.contentsScroll}>
                        {step === 'form' && (
                            <>
                                <div className={localClasses.header}>
                                    <Typography variant="h2">
                                        Start building community resilience today.
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                    >
                                        Better community resilience is just a
                                        step away! Fill out the form below to
                                        get started. We'll review your
                                        request and follow up within two
                                        business days.
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                    >
                                        * Required fields
                                    </Typography>
                                </div>
                                <InterestForm
                                    onSubmit={handleInterestSubmit}
                                />
                            </>
                        )}
                        {step === 'payment' && stripeOptions?.clientSecret && (
                            <Stack
                                gap="1.5rem"
                                sx={{ width: '100%', minWidth: 0 }}
                            >
                                {pricingQuote && submittedValues && (
                                    <InterestPricingQuote
                                        quote={pricingQuote}
                                        subscription_type={
                                            submittedValues.subscription_type
                                        }
                                    />
                                )}
                                <StripeContainer options={stripeOptions}>
                                    <InterestStripeSetup
                                        onSuccess={() => setStep('success')}
                                        onSkip={() => setStep('success')}
                                    />
                                </StripeContainer>
                            </Stack>
                        )}
                        {step === 'success' && (
                            <Stack
                                gap="1.5rem"
                                alignItems="center"
                                className={localClasses.successContainer}
                                sx={{ width: '100%', minWidth: 0 }}
                            >
                                <IoCheckmarkCircle
                                    color={theme.palette.success.main}
                                    size={96}
                                />
                                <Typography
                                    component="h1"
                                    variant={mdScreens ? 'h3' : 'h4'}
                                    textAlign="center"
                                >
                                    Thanks for reaching out!
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    textAlign="center"
                                >
                                    Our team will review your details and
                                    follow up within two business days to help
                                    you onboard.
                                </Typography>
                                {demoPricingSummary ? (
                                    <Stack
                                        gap="0.75rem"
                                        sx={{
                                            width: '100%',
                                            maxWidth: '29rem',
                                            p: 2,
                                            borderRadius: '0.75rem',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            color="text.secondary"
                                        >
                                            Estimated list pricing for your
                                            community size (reference)
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {getInterestCommunitySizeLabel(
                                                demoPricingSummary.community_size
                                            )}
                                        </Typography>
                                        <Stack gap={0.5}>
                                            <Typography variant="body1">
                                                <strong>Monthly:</strong>{' '}
                                                {formatInterestPricingAmount(
                                                    demoPricingSummary.monthly
                                                )}
                                            </Typography>
                                            <Typography variant="body1">
                                                <strong>Annual:</strong>{' '}
                                                {formatInterestPricingAmount(
                                                    demoPricingSummary.yearly
                                                )}
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            variant="caption"
                                            color="success.main"
                                            fontWeight={600}
                                        >
                                            Annual plan includes two months free
                                        </Typography>
                                    </Stack>
                                ) : null}
                                {pricingQuote && submittedValues ? (
                                    <InterestPricingQuote
                                        quote={pricingQuote}
                                        subscription_type={
                                            submittedValues.subscription_type
                                        }
                                    />
                                ) : null}
                                <Button
                                    variant="outlined"
                                    onClick={handleStartNew}
                                >
                                    Submit another request
                                </Button>
                            </Stack>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

const useLocalStyles = makeStyles()({
    container: {
        background:
            'linear-gradient(180deg, rgba(0, 0, 87, 0.33) 0%, rgba(255, 255, 255, 0) 100%)',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        flex: 1,
        minHeight: 0,
        padding: '1rem',
        boxSizing: 'border-box',
        [theme.breakpoints.up('md')]: {
            // Viewport minus site header, footer, and page padding
            height: 'calc(100dvh - 8rem)',
            maxHeight: 'calc(100dvh - 8rem)',
            overflow: 'hidden',
        },
    },
    mainContent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch',
        gap: '1rem',
        flex: 1,
        minHeight: 0,
        [theme.breakpoints.up('md')]: {
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
            alignItems: 'stretch',
            columnGap: theme.spacing(3),
            boxSizing: 'border-box',
            overflow: 'hidden',
            height: '100%',
        },
    },
    heroColumn: {
        display: 'none',
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 0,
            overflow: 'hidden',
        },
    },
    heroImage: {
        width: '100%',
        maxWidth: '100%',
        height: 'auto',
        maxHeight: '100%',
        objectFit: 'contain',
        display: 'block',
        minWidth: 0,
    },
    contents: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        [theme.breakpoints.up('md')]: {
            gap: theme.spacing(1.5),
            minHeight: 0,
            overflow: 'hidden',
        },
    },
    contentsScroll: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        [theme.breakpoints.up('md')]: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: theme.spacing(0.5),
        },
    },
    rpLogo: {
        flexShrink: 0,
        width: 'min(60%, 20rem)',
        height: 'auto',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        flexShrink: 0,
        textAlign: 'center',
        [theme.breakpoints.up('md')]: {
            textAlign: 'left',
        },
    },
    successContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '1.5rem',
        padding: '2rem',
        boxShadow: theme.shadows[3],
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
})
